import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkIcon from "@mui/icons-material/Link";
import CircleIcon from "@mui/icons-material/Circle";
import Axios from "../../Common/api/axiosConfig";
import { onActiveStoreChange } from "../../utils/storeHelper";

export interface StoreData {
  name: string;
  description: string;
  number: string;
  url: string;
  status: string;
}

const BreadcrumbsComponent = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [storeData, setStoreData] = useState<StoreData | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    fetchStoreData();
    const unsubscribe = onActiveStoreChange(() => {
      fetchStoreData();
    });
    return () => unsubscribe();
  }, []);

  const fetchStoreData = async () => {
    try {
      setFetchLoading(true);

      // Get userData from localStorage and parse it
      const userDataString = localStorage.getItem("userData");

      if (!userDataString) {
        setError("User data not found. Please login again.");
        setOpenSnackbar(true);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData._id || userData.id;

      if (!userId) {
        setError("User ID not found in user data.");
        setOpenSnackbar(true);
        return;
      }

      // Correct API call with proper query parameter
      const response = await Axios.get(`/store/${userId}`);
      const storeData = response.data?.store;

      // console.log("Fetched store data:", storeData);
      setStoreData(storeData);
    } catch (error: any) {
      console.error("Error fetching store data:", error);

      if (error.response?.status === 404) {
        setError("Store not found. Please create a store first.");
      } else if (error.response) {
        setError("Failed to load store data. Please try again.");
      } else if (error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("An unexpected error occurred.");
      }
      setOpenSnackbar(true);
    } finally {
      setFetchLoading(false);
    }
  };

  const StatusChip = ({ status }: { status: string }) => (
    <Chip
      icon={<CircleIcon sx={{ fontSize: 10 }} />}
      label={status === "active" ? "Active" : "Inactive"}
      variant="outlined"
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "13px",
        height: "28px",
        backgroundColor:
          status === "active"
            ? isDark
              ? "rgba(46, 125, 50, 0.15)"
              : "#e8f5e9"
            : isDark
            ? "rgba(255,255,255,0.08)"
            : "#f5f5f5",
        color:
          status === "active"
            ? isDark
              ? "#81c784"
              : "#1b5e20"
            : isDark
            ? "#ccc"
            : "#666",
        borderColor:
          status === "active"
            ? isDark
              ? "rgba(129,199,132,0.3)"
              : "#c8e6c9"
            : isDark
            ? "rgba(255,255,255,0.2)"
            : "#e0e0e0",
        "& .MuiChip-icon": {
          color:
            status === "active"
              ? isDark
                ? "#81c784"
                : "#1b5e20"
              : isDark
              ? "#999"
              : "#9e9e9e",
          marginLeft: "6px",
        },
      }}
    />
  );

  // Show loading state
  if (fetchLoading) {
    return (
      <Card
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: 3,
          backgroundColor: isDark ? theme.palette.background.paper : "#fff",
          boxShadow: isDark
            ? "0px 1px 3px rgba(0,0,0,0.6)"
            : "0px 1px 5px rgba(0,0,0,0.1)",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography>Loading store data...</Typography>
      </Card>
    );
  }

  // Show error state if no store data
  if (!storeData) {
    return (
      <Card
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: 3,
          backgroundColor: isDark ? theme.palette.background.paper : "#fff",
          boxShadow: isDark
            ? "0px 1px 3px rgba(0,0,0,0.6)"
            : "0px 1px 5px rgba(0,0,0,0.1)",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography color="error">
          {error || "No store data available. Please create a store first."}
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        p: isMobile ? 2 : 3,
        borderRadius: 3,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "space-between",
        backgroundColor: isDark ? theme.palette.background.paper : "#fff",
        boxShadow: isDark
          ? "0px 1px 3px rgba(0,0,0,0.6)"
          : "0px 1px 5px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.palette.divider}`,
        gap: isMobile ? 2 : 0,
      }}
    >
      {/* Left Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? 2 : 3,
          width: "100%",
        }}
      >
        {/* Store Icon */}
        <Box
          sx={{
            backgroundColor: isDark ? "#1565c0" : "#2979ff",
            borderRadius: "16px",
            width: isMobile ? 50 : 64,
            height: isMobile ? 50 : 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StoreIcon sx={{ fontSize: isMobile ? 28 : 36, color: "#fff" }} />
        </Box>

        {/* Store Details */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{
              fontWeight: 700,
              color: isDark ? "#fff" : "#222",
              fontSize: isMobile ? "16px" : "18px",
            }}
          >
            {storeData.name || "Store Name"}
          </Typography>
          <Typography
            sx={{
              color: isDark ? "#aaa" : "#666",
              fontSize: isMobile ? "13px" : "15px",
            }}
          >
            {storeData.description || "No description available"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 2,
              mt: 1,
            }}
          >
            {/* WhatsApp */}
            {storeData.number && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} />
                <Typography
                  sx={{
                    color: isDark ? "#ddd" : "#444",
                    fontSize: "14px",
                  }}
                >
                  {storeData.number}
                </Typography>
              </Box>
            )}

            {/* Store Link */}
            {storeData.url && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <LinkIcon
                  sx={{ fontSize: 18, color: isDark ? "#aaa" : "#666" }}
                />
                <Link
                  href={`https://app.therudrix.in/${storeData.url}`}
                  //href={`http://localhost:3000/${storeData.url}`}
                  sx={{
                    color: isDark ? "#90caf9" : "#2979ff",
                    fontSize: "14px",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {storeData.url}
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right Section - Status */}
      <Box sx={{ mt: isMobile ? 2 : 0 }}>
        <StatusChip status={storeData.status || "inactive"} />
      </Box>
    </Card>
  );
};

export default BreadcrumbsComponent;
